import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, MenuItem } from '@mui/material';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import { StudioAddressBarcodeQuery } from '../../../services/query/studioService/StudioAddressBarcodeQuery';
import { StudioAddressBarcodeInformation as StudioAddressBarcodeInfoType } from '../../../types/StudioAddressBarcode';
import Swal from 'sweetalert2';

export const StudioAddressBarcodeInformation = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<StudioAddressBarcodeInfoType[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const searchBarcodeRef = useRef<HTMLInputElement>(null);
    const searchAddressRef = useRef<HTMLInputElement>(null);
    const isActiveRef = useRef<HTMLInputElement>(null);
    const [filterKey, setFilterKey] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);

    // Actual filters used for fetching data
    const [appliedFilters, setAppliedFilters] = useState({
        addressBarcode: '',
        barcode: '',
        isActive: 'all',
    });

    useEffect(() => {
        if (hasSearched) {
            fetchData();
        }
    }, [currentPage, pageSize, appliedFilters, hasSearched]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const params: any = {
                PageNumber: currentPage + 1,
                PageSize: pageSize,
            };

            if (appliedFilters.addressBarcode) params.AddressBarcode = appliedFilters.addressBarcode;
            if (appliedFilters.barcode) params.Barcode = Number(appliedFilters.barcode);
            if (appliedFilters.isActive === 'active') params.IsActive = true;
            if (appliedFilters.isActive === 'inactive') params.IsActive = false;

            const response = await StudioAddressBarcodeQuery.GetStudioAddressBarcodesInformation(params);

            if (response) {
                const rawItems = response.items || response.Items || [];
                const total = response.totalCount || response.TotalCount || 0;

                const items = rawItems.map((item: any) => ({
                    addressBarcodeInfoId: item.addressBarcodeInfoId || item.AddressBarcodeInfoId,
                    addressBarcode: item.addressBarcode || item.AddressBarcode,
                    barcode: item.barcode || item.Barcode,
                    active: item.isActive,
                    createDate: item.createDate || item.CreateDate,
                    id: item.id || item.Id || item.addressBarcodeInfoId || item.AddressBarcodeInfoId,
                }));

                setData(items);
                setTotalCount(total);
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studioAddressBarcode.fetchError'),
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(0);
        setHasSearched(true);
        // Apply current input values to filters which triggers useEffect
        setAppliedFilters({
            addressBarcode: searchAddressRef.current?.value || '',
            barcode: searchBarcodeRef.current?.value || '',
            isActive: isActiveRef.current?.value || 'all',
        });
    };

    const breadcrumbList = [
        { label: t('general.home'), path: '/home' },
        { label: t('studio.title'), path: '/studio' },
        { label: 'Address Barcode Information' },
    ];

    const getRowId = (row: any) => {
        return row.addressBarcodeInfoId || row.id || row.Id;
    };

    const columns = useMemo(
        () => [
            {
                field: 'addressBarcode',
                headerName: t('studioAddressBarcode.barcode'),
                width: 200,
            },
            {
                field: 'barcode',
                headerName: 'Barkod', // Using explicit label as per request, or t('studioAddressBarcode.address') if mapped
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'active',
                headerName: t('studioAddressBarcode.isActive'),
                width: 100,
                renderCell: (params: any) => (
                    <span style={{ color: params.value ? 'green' : 'red' }}>
                        {params.value ? t('general.active') : t('general.passive')}
                    </span>
                ),
            },
            {
                field: 'createDate',
                headerName: 'Oluşturulma Tarihi',
                width: 200,
                renderCell: (params: any) => (params.value ? new Date(params.value).toLocaleString() : '-'),
            },
        ],
        [t]
    );

    return (
        <SCUIContent>
            <>
                {isLoading && <SCUILoadingLinear />}
                <SCUIBreadCrumb items={breadcrumbList} />

                <Box className="studio-address-barcode-container" sx={{ p: 2 }}>
                    <Box className="filters-section" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TextField
                            label={t('studioAddressBarcode.barcode')}
                            inputRef={searchAddressRef}
                            defaultValue=""
                            size="small"
                            style={{ marginRight: 10, width: 200 }}
                        />
                        <TextField
                            label="Barkod"
                            inputRef={searchBarcodeRef}
                            defaultValue=""
                            size="small"
                            type="number"
                            style={{ marginRight: 10, width: 200 }}
                        />
                        <TextField
                            key={filterKey}
                            select
                            label={t('studioAddressBarcode.isActive')}
                            defaultValue="all"
                            inputRef={isActiveRef}
                            size="small"
                            style={{ marginRight: 10, width: 150 }}
                        >
                            <MenuItem value="all">{t('general.all')}</MenuItem>
                            <MenuItem value="active">{t('general.active')}</MenuItem>
                            <MenuItem value="inactive">{t('general.passive')}</MenuItem>
                        </TextField>
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            size="m"
                            onClick={handleSearch}
                            style={{ marginRight: 10 }}
                            sizeType={'fixed'}
                        />
                        <SCUIButton
                            text={t('general.clear')}
                            type="secondary"
                            size="m"
                            onClick={() => {
                                if (searchBarcodeRef.current) searchBarcodeRef.current.value = '';
                                if (searchAddressRef.current) searchAddressRef.current.value = '';
                                setFilterKey((prev) => prev + 1);
                                setAppliedFilters({
                                    barcode: '',
                                    addressBarcode: '',
                                    isActive: 'all',
                                });
                                setCurrentPage(0);
                            }}
                            sizeType={'fixed'}
                        />
                    </Box>

                    <Box className="grid-section">
                        <SCUICustomDataGrid
                            rows={data}
                            columns={columns}
                            pageSize={pageSize}
                            onPageSizeChange={setPageSize}
                            page={currentPage}
                            onPageChange={setCurrentPage}
                            rowCount={totalCount}
                            getRowId={getRowId}
                            paginationModeChange
                            loading={isLoading}
                        />
                    </Box>
                </Box>
            </>
        </SCUIContent>
    );
};
